/** Chart of accounts (M03-01–02): the tree, with add and edit. */
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Lock, Pencil, Plus } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { ACCOUNT_TYPES, TYPE_LABEL, useAccounts, type Account } from '../../lib/queries';
import { Alert, Button, Card, Field, FilterBar, Modal, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, filterClass, inputClass } from '../../components/ui';

const SUBTYPES: Record<Account['type'], string[]> = {
  ASSET: ['CASH', 'RECEIVABLE', 'INVENTORY', 'PREPAYMENT', 'TAX_ASSET', 'CURRENT_ASSET', 'PPE', 'ACCUM_DEPRECIATION', 'NONCURRENT_ASSET', 'HEADER'],
  LIABILITY: ['PAYABLE', 'ACCRUAL', 'TAX_LIABILITY', 'CURRENT_LIABILITY', 'NONCURRENT_LIABILITY', 'HEADER'],
  EQUITY: ['CAPITAL', 'RETAINED_EARNINGS', 'DRAWINGS', 'SUSPENSE', 'HEADER'],
  INCOME: ['REVENUE', 'CONTRA_REVENUE', 'OTHER_INCOME', 'HEADER'],
  EXPENSE: ['COST_OF_SALES', 'OPERATING_EXPENSE', 'OTHER_EXPENSE', 'INCOME_TAX', 'HEADER'],
};

const label = (s: string): string => s.toLowerCase().replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

function depthMap(accounts: Account[]): Map<string, number> {
  const byId = new Map(accounts.map((a) => [a.id, a]));
  const depth = new Map<string, number>();
  const of = (a: Account): number => {
    if (depth.has(a.id)) return depth.get(a.id)!;
    const d = a.parentId && byId.has(a.parentId) ? of(byId.get(a.parentId)!) + 1 : 0;
    depth.set(a.id, d);
    return d;
  };
  accounts.forEach(of);
  return depth;
}

export function AccountsPage(): JSX.Element {
  const { can } = useAuth();
  const accounts = useAccounts();
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<Account | 'new' | null>(null);

  const depth = useMemo(() => depthMap(accounts.data ?? []), [accounts.data]);
  const rows = (accounts.data ?? []).filter(
    (a) =>
      (showInactive || a.active) &&
      (!type || a.type === type) &&
      (!q.trim() || `${a.code} ${a.name}`.toLowerCase().includes(q.trim().toLowerCase())),
  );

  return (
    <>
      <PageHeader
        title="Chart of Accounts"
        description="Header accounts group the ones under them. Control accounts only take postings from their sub-ledgers."
        actions={can('coa.manage') && <Button onClick={() => setEditing('new')}><Plus aria-hidden="true" className="h-4 w-4" /> New account</Button>}
      />
      <FilterBar>
        <input className={`${filterClass} w-56`} placeholder="Code or name" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search accounts" />
        <select className={filterClass} value={type} onChange={(e) => setType(e.target.value)} aria-label="Type">
          <option value="">All types</option>
          {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <input type="checkbox" className="h-4 w-4" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} /> Show inactive
        </label>
      </FilterBar>
      <Card>
        {accounts.isLoading && <Skeleton className="h-96 w-full" />}
        {accounts.data && (
          <TableWrap>
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800">
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Type</Th>
                <Th>Role</Th>
                <Th>{''}</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((a) => (
                <tr key={a.id} className={a.active ? '' : 'opacity-50'}>
                  <Td className={`whitespace-nowrap tabular-nums ${a.isPosting ? '' : 'font-bold'}`}>{a.code}</Td>
                  <Td className={a.isPosting ? '' : 'font-bold'}>
                    <span style={{ paddingLeft: `${(depth.get(a.id) ?? 0) * 1.25}rem` }} className="inline-flex items-center gap-2">
                      {a.name}
                      {a.isControl && (
                        <span title="Control account: sub-ledger postings only">
                          <Lock aria-label="Control account" className="h-3.5 w-3.5 text-amber-600" />
                        </span>
                      )}
                    </span>
                  </Td>
                  <Td className="whitespace-nowrap text-slate-500">{label(a.subType)}</Td>
                  <Td>{a.systemRole ? <StatusChip tone="info">{a.systemRole}</StatusChip> : null}</Td>
                  <Td align="right">
                    {can('coa.manage') && (
                      <button className="grid h-9 w-9 place-items-center rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800" aria-label={`Edit ${a.code}`} onClick={() => setEditing(a)}>
                        <Pencil aria-hidden="true" className="h-4 w-4" />
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Card>
      {editing && <AccountDialog account={editing === 'new' ? null : editing} accounts={accounts.data ?? []} onClose={() => setEditing(null)} />}
    </>
  );
}

function AccountDialog({ account, accounts, onClose }: { account: Account | null; accounts: Account[]; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    code: account?.code ?? '',
    name: account?.name ?? '',
    type: account?.type ?? ('EXPENSE' as Account['type']),
    subType: account?.subType ?? 'OPERATING_EXPENSE',
    parentId: account?.parentId ?? '',
    isPosting: account?.isPosting ?? true,
    isCash: account?.isCash ?? false,
    description: account?.description ?? '',
    active: account?.active ?? true,
  });
  const [error, setError] = useState<ApiError | null>(null);
  const headers = accounts.filter((a) => !a.isPosting && a.type === form.type && a.id !== account?.id);
  const set = (patch: Partial<typeof form>): void => setForm((f) => ({ ...f, ...patch }));

  const save = useMutation({
    mutationFn: () => {
      const body = { ...form, parentId: form.parentId || null };
      if (account) {
        const { isPosting: _ignored, ...patch } = body;
        void _ignored;
        return api(`/accounts/${account.id}`, { method: 'PATCH', body: patch });
      }
      const { active: _a, ...create } = body;
      void _a;
      return api('/accounts', { method: 'POST', body: create });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      onClose();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught : new ApiError(0, 'Could not save.')),
  });

  const locked = Boolean(account?.inUse);

  return (
    <Modal title={account ? `Edit ${account.code} ${account.name}` : 'New account'} onClose={onClose}>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {error && <Alert tone="critical">{error.message}</Alert>}
        {locked && <Alert tone="info">This account has postings, so its code and type are fixed.</Alert>}
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Code" required error={error?.fields.code}>
            <input className={inputClass} value={form.code} disabled={locked} onChange={(e) => set({ code: e.target.value })} />
          </Field>
          <Field label="Type" required>
            <select className={inputClass} value={form.type} disabled={locked} onChange={(e) => { const t = e.target.value as Account['type']; set({ type: t, subType: SUBTYPES[t][0], parentId: '' }); }}>
              {ACCOUNT_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Name" required error={error?.fields.name}>
          <input className={inputClass} value={form.name} onChange={(e) => set({ name: e.target.value })} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Classification">
            <select className={inputClass} value={form.subType} onChange={(e) => set({ subType: e.target.value })}>
              {SUBTYPES[form.type].map((s) => <option key={s} value={s}>{label(s)}</option>)}
            </select>
          </Field>
          <Field label="Under" error={error?.fields.parentId}>
            <select className={inputClass} value={form.parentId} onChange={(e) => set({ parentId: e.target.value })}>
              <option value="">Top level</option>
              {headers.map((h) => <option key={h.id} value={h.id}>{h.code} — {h.name}</option>)}
            </select>
          </Field>
        </div>
        {!account && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4" checked={!form.isPosting} onChange={(e) => set({ isPosting: !e.target.checked })} /> Header account (groups others, no postings)
          </label>
        )}
        {form.type === 'ASSET' && form.isPosting && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4" checked={form.isCash} onChange={(e) => set({ isCash: e.target.checked })} /> Cash or bank account (appears in cash reports and the Cash Receipts/Disbursements books)
          </label>
        )}
        {account && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="h-4 w-4" checked={form.active} onChange={(e) => set({ active: e.target.checked })} /> Active
          </label>
        )}
        <Field label="Description">
          <textarea className={inputClass} rows={2} value={form.description} onChange={(e) => set({ description: e.target.value })} />
        </Field>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" busy={save.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}
